import { HttpService } from '@/api/shared/http.service'
import { ApiPagedPayload, ApiPagedResponse } from '@/api/shared/types'
import {
  migrateFromV1ToV2,
  migrateFromV1ToV3,
  migrateFromV1ToV4
} from '@/api/shared/utils'

import { FinanceBalanceMapper } from './balance/balance.mapper'
import type {
  ActsListPayload,
  Balance,
  BalanceInvoice,
  BalancePayDTO,
  BalancePayload,
  BalancesByTypePayload,
  BalancesInvoicesPayload,
  ContractsResponse,
  CustomerFinanceAct,
  CustomersResponse,
  DetailedBalance,
  FinanceBalanceTicketDomainType,
  FinanceInvoice,
  FinanceInvoiceCustomerResponse,
  FinanceInvoiceFile,
  FinanceInvoicePremiseResponse,
  FinanceInvoiceType,
  FinancePayDTO,
  FinancePayResponseType,
  InvoicePaymentMethods,
  InvoicesListPayload,
  PaymentDetail,
  PersonalAccount
} from './finance.types'

class FinanceService extends HttpService {
  async getFinanceBalance(projectId: string) {
    const { data } = await this.axios.get<FinanceBalanceTicketDomainType[]>(
      this.getPath('/invoices/balance'),
      {
        params: { projectId }
      }
    )

    return data ? FinanceBalanceMapper.toEntity(data) : null
  }

  async getInvoices(params: ApiPagedPayload<InvoicesListPayload>) {
    const { data, statusText } = await this.axios.get<
      ApiPagedResponse<FinanceInvoice[]>
    >(migrateFromV1ToV4(this.getPath('/invoices')), {
      params
    })

    return { statusText, data }
  }

  async getBalances({ projectId, personalAccountId }: BalancePayload) {
    const { data } = await this.axios.get<ApiPagedResponse<Balance[]>>(
      this.getPath('/balances'),
      {
        params: { projectId, personalAccountId }
      }
    )

    return data
  }

  async getBalancesByType({
    projectId,
    personalAccountId,
    alias
  }: BalancesByTypePayload) {
    const { data } = await this.axios.get<DetailedBalance>(
      this.getPath(`/balances/${alias}`),
      {
        params: { projectId, personalAccountId }
      }
    )

    return data
  }

  async getBalancesInvoices({
    projectId,
    balanceTypeAlias,
    personalAccountId,
    page,
    periodFrom,
    periodTo
  }: ApiPagedPayload<BalancesInvoicesPayload>) {
    const { data } = await this.axios.get<ApiPagedResponse<BalanceInvoice[]>>(
      this.getPath('/balances/invoices'),
      {
        params: {
          projectId,
          balanceTypeAlias,
          personalAccountId,
          page,
          periodFrom,
          periodTo
        }
      }
    )

    return data
  }

  async getPersonalAccounts(projectId: string) {
    const { data } = await this.axios.get<{ data: PersonalAccount[] }>(
      this.getPath('/personalAccounts'),
      {
        params: { projectId }
      }
    )

    return data.data
  }

  async getInvoicesTypes(projectId: string) {
    const { data } = await this.axios.get<FinanceInvoiceType[]>(
      this.getPath('/invoices/types'),
      {
        params: { projectId }
      }
    )
    return data
  }

  async getPaymentDetail({
    projectId,
    balanceTypeAlias,
    personalAccountId,
    invoiceId
  }: BalancesInvoicesPayload) {
    const { data } = await this.axios.get<PaymentDetail>(
      this.getPath(`/balances/${balanceTypeAlias}/paymentDetail`),
      {
        params: { projectId, personalAccountId, invoiceId }
      }
    )

    return data
  }

  async getInvoicesPremises(projectId: string) {
    const { data } = await this.axios.get<FinanceInvoicePremiseResponse>(
      this.getPath('/invoices/premises'),
      {
        params: { projectId }
      }
    )
    return data
  }

  async getInvoicesCustomers(projectId: string) {
    const { data } = await this.axios.get<FinanceInvoiceCustomerResponse>(
      this.getPath('/invoices/customers'),
      {
        params: { projectId }
      }
    )
    return data
  }

  async getInvoiceFile({ invoiceId }: { invoiceId: string }) {
    return this.makeSafeRequest<FinanceInvoiceFile>(
      {
        url: this.getPath(`/invoices/${invoiceId}/file`)
      },
      { okIfStatus: 200 }
    )
  }

  async getInvoicePaymentMethods({ invoiceId }: { invoiceId?: string }) {
    if (!invoiceId) {
      return
    }
    const { data } = await this.axios.get<InvoicePaymentMethods>(
      migrateFromV1ToV2(this.getPath(`/invoices/${invoiceId}/paymentMethods`))
    )
    return data
  }

  async makePayment(data: BalancePayDTO | FinancePayDTO) {
    return this.axios.post<FinancePayResponseType>(
      migrateFromV1ToV3(this.getPath('/payment/acquiring')),
      data
    )
  }

  async deleteCard(cardId: string) {
    return this.axios.delete(this.getPath(`/cards/${cardId}`))
  }

  async getActsCustomers(projectId: string) {
    const { data } = await this.axios.get<CustomersResponse>(
      this.getPath('/acts/customers'),
      {
        params: { projectId }
      }
    )
    return data
  }

  async getActsContracts(projectId: string) {
    const { data } = await this.axios.get<ContractsResponse>(
      this.getPath('/acts/contracts'),
      {
        params: { projectId }
      }
    )
    return data
  }

  async getActs(params: ApiPagedPayload<ActsListPayload>) {
    const { data } = await this.axios.get<
      ApiPagedResponse<CustomerFinanceAct[]>
    >(migrateFromV1ToV4(this.getPath('/acts')), {
      params
    })

    return data
  }

  async getInvoiceById(invoiceId: string) {
    const { data } = await this.axios.get<FinanceInvoice>(
      this.getPath(`/invoices/${invoiceId}`)
    )
    return data
  }
}

const FinanceServiceSingleton = new FinanceService('/api/v1/finances')
export { FinanceServiceSingleton as FinanceService }
